import type { SiteConfig, LanguageContent, SiteVersion } from "@/types/sites";
import { resolveAssetUrl } from "@/utils/assetUrl";

export interface ViewerManifest {
  siteId: string;
  versionId: string;
  publishedAt: string;
  defaultLanguage: string;
  availableLanguages: string[];
  config: SiteConfig | null;
  languages: Record<string, LanguageContent>;
  suspension?: {
    active: boolean;
    message?: string;
  };
}

const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const manifestBase = (import.meta.env.VITE_VIEWER_MANIFEST_BASE_URL || "").replace(/\/$/, "");

function normaliseHost(hostname: string): string {
  return hostname.trim().toLowerCase();
}

function shouldUseManifestBase(path: string): boolean {
  if (!manifestBase) return false;
  if (import.meta.env.PROD !== true) return false;
  // Dev manifests should always resolve from the legacy bucket path.
  if (path.startsWith("hosts/dev-")) return false;
  return true;
}

function normalizeLanguageValue(value: string | undefined): string {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      const segments = url.pathname.split("/").filter(Boolean);
      if (segments.length > 0) {
        return decodeURIComponent(segments[segments.length - 1] || "");
      }
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}

function normalizeManifestLanguages(
  languages: Record<string, LanguageContent> | undefined
): Record<string, LanguageContent> {
  const normalized: Record<string, LanguageContent> = {};
  const source = languages || {};

  for (const [key, content] of Object.entries(source)) {
    const normalizedKey = normalizeLanguageValue(key) || key;
    if (!normalized[normalizedKey]) {
      normalized[normalizedKey] = content;
    }
  }

  return normalized;
}

function buildBaseUrl(path: string): string {
  if (shouldUseManifestBase(path)) {
    return `${manifestBase}/${path}`;
  }

  if (!bucket) {
    throw new Error("Missing VITE_FIREBASE_STORAGE_BUCKET for manifest access");
  }

  return resolveAssetUrl(path) || `https://storage.googleapis.com/${bucket}/${path}`;
}

export function buildHostManifestUrl(hostname: string): string {
  const host = normaliseHost(hostname);
  if (host.endsWith(".localhost")) {
    // Development hosts default to the site manifest, rely on siteId lookup instead
    return "";
  }
  return buildBaseUrl(`hosts/${host}/manifest.json`);
}

export function buildSiteManifestUrl(siteId: string): string {
  return buildBaseUrl(`sites/${siteId}/published/manifest.json`);
}

async function fetchManifestFromUrl(url: string): Promise<ViewerManifest> {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const error = text || response.statusText || "Manifest fetch failed";
    throw new Error(`${response.status} ${error}`.trim());
  }

  return (await response.json()) as ViewerManifest;
}

export async function fetchManifestByHost(host: string): Promise<ViewerManifest> {
  return fetchManifestFromUrl(buildHostManifestUrl(host));
}

export async function fetchManifestBySiteId(siteId: string): Promise<ViewerManifest> {
  return fetchManifestFromUrl(buildSiteManifestUrl(siteId));
}

export interface AssembledViewerVersion {
  version: SiteVersion;
  resolvedLanguage: string;
}

export function assembleSiteVersion(
  manifest: ViewerManifest,
  requestedLanguage?: string
): AssembledViewerVersion {
  const normalizedLanguages = normalizeManifestLanguages(manifest.languages);
  const availableLanguages = Array.from(
    new Set([
      ...(manifest.availableLanguages || []).map((value) => normalizeLanguageValue(value)),
      ...Object.keys(normalizedLanguages),
    ])
  ).filter(Boolean);

  const normalizedRequestedLanguage = normalizeLanguageValue(requestedLanguage);
  const normalizedDefaultLanguage = normalizeLanguageValue(manifest.defaultLanguage);
  const defaultLanguage = normalizedDefaultLanguage || availableLanguages[0] || "en";
  const resolvedLanguage = normalizedRequestedLanguage && normalizedLanguages[normalizedRequestedLanguage]
    ? normalizedRequestedLanguage
    : normalizedLanguages[defaultLanguage]
      ? defaultLanguage
      : availableLanguages.find((lang) => normalizedLanguages[lang]) || defaultLanguage;

  const assembled = {
    id: manifest.versionId,
    versionId: manifest.versionId,
    name: "Published Version",
    status: "published",
    availableLanguages,
    defaultLanguage,
    configPath: "",
    languageContentPaths: {},
    config: manifest.config || undefined,
    languages: normalizedLanguages,
    createdAt: manifest.publishedAt,
    updatedAt: manifest.publishedAt,
    generatedAt: manifest.publishedAt,
  } as unknown as SiteVersion;

  return {
    version: assembled,
    resolvedLanguage,
  };
}
